import React from "react";

import Button from "components/Button";
import type { ApiUser } from "context/UserContext";
import { formatPhoneNumber } from "helpers/phone";
import VerificationStatus from "./VerificationStatus";

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "Unknown";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return "Invalid Date";
  }
};

type ProfileSectionProps = {
  user: ApiUser;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isRequestingVerification: boolean;
  onVerifyRequest: (payload: {
    method: "email" | "sms";
    target: string;
    intent: "verify_email" | "verify_phone";
  }) => void;
  onChangeRequest: (type: "email" | "phone") => void;
};

const ProfileSection: React.FC<ProfileSectionProps> = ({
  user,
  isEmailVerified,
  isPhoneVerified,
  isRequestingVerification,
  onVerifyRequest,
  onChangeRequest,
}) => {
  return (
    <div className="rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 font-serif text-[19px] font-normal text-soil">
        Profile
      </h2>
      <div className="divide-y divide-oat-100">
        {/* Name Row */}
        {user.first_name && user.last_name && (
          <div className="py-2 first:pt-0">
            <div className="text-xs text-oat-800">Name</div>
            <div className="break-words text-sm font-bold text-soil">
              {user.first_name} {user.last_name}
            </div>
          </div>
        )}

        {/* Email Row */}
        {user.email ? (
          <div className="py-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-oat-800">Email</div>
              {isEmailVerified ? (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => onChangeRequest("email")}
                  className="ml-4 shrink-0"
                >
                  Update
                </Button>
              ) : (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() =>
                    onVerifyRequest({
                      method: "email",
                      target: user.email!,
                      intent: "verify_email",
                    })
                  }
                  disabled={isRequestingVerification}
                  className="ml-4 shrink-0"
                >
                  {isRequestingVerification ? "Sending..." : "Verify"}
                </Button>
              )}
            </div>
            <VerificationStatus
              type="email"
              value={user.email}
              isVerified={isEmailVerified}
            />
          </div>
        ) : (
          <div className="py-2 text-sm text-oat-700">Email not set</div>
        )}

        {/* Phone Row */}
        {user.phone ? (
          <div className="py-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-oat-800">Phone</div>
              {isPhoneVerified ? (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => onChangeRequest("phone")}
                  className="ml-4 shrink-0"
                >
                  Update
                </Button>
              ) : (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() =>
                    onVerifyRequest({
                      method: "sms",
                      target: user.phone!,
                      intent: "verify_phone",
                    })
                  }
                  disabled={isRequestingVerification}
                  className="ml-4 shrink-0"
                >
                  {isRequestingVerification ? "Sending..." : "Verify"}
                </Button>
              )}
            </div>
            <VerificationStatus
              type="phone"
              value={formatPhoneNumber(user.phone)}
              isVerified={isPhoneVerified}
            />
          </div>
        ) : (
          <div className="py-2">
            <div className="text-xs text-oat-800">Phone</div>
            <div className="text-sm text-oat-700">Not set</div>
          </div>
        )}

        {/* Member Since Row */}
        <div className="py-2 last:pb-0">
          <div className="text-xs text-oat-800">Member since</div>
          <div className="text-sm font-bold text-soil">
            {formatDate(user.created_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;
